// features/chat-workspace-feature.js — WORKSPACE SESSION WRAPPER
// ═══════════════════════════════════════════════════════════
// Thin wrapper that renders the new Workstation component inside the
// app shell's feature slot. Used by pages/index.js when
// activeFeature === 'workspace' AND a workspace session is active.
//
// Phase 2 architecture:
//   - This file used to be a copy of chat-space-feature.js with the
//     3-tab layout (Raw / Lens / Depth). In Phase 1 it shipped
//     unchanged as a placeholder.
//   - In Phase 2 it's replaced entirely with a Workstation render.
//     The Workstation handles all the layout, data, chat, canvas,
//     journey map, and Layer 2 internally.
//
// Deleted from Phase 1:
//   - ShiftThreadHeader + tab selector
//   - ChatWorkspaceRaw / ChatWorkspaceSynthesis / ChatWorkspaceDepth
//     imports and renders (these files stay in the repo as dead code
//     for now; they can be deleted in a cleanup pass)
//   - useVoice integration
//   - ThreadArtifactPanel
//   - ContentActionMenu
//   - Toast logic for synthesis findings
//
// All of the above will come back in later phases if/when the
// Workstation needs them, reimplemented in the new architecture.
// ═══════════════════════════════════════════════════════════

import { forwardRef, useImperativeHandle } from 'react';
import Workstation from './workstation';

const ChatWorkspaceFeature = forwardRef(function ChatWorkspaceFeature({
  userId,
  supabase,
  userRecord,
  spaceSession,
  voiceConfig,        // threaded through to the workstation chat panel for voice input
  onLeave,
  onSidebarCollapse,
  // Accepted for API compatibility with the old signature, unused in Phase 2.5:
  // currentSessionId, setCurrentSessionId, threadHistory, setThreadHistory, onAskShift
}, ref) {

  // Expose a minimal imperative API for shell compatibility
  useImperativeHandle(ref, () => ({
    // Placeholder — the workstation manages its own state. If the shell
    // needs to trigger something later (e.g., a "new thread" action)
    // we add it here.
  }));

  if (!spaceSession) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: '#555',
        fontSize: 13,
        fontStyle: 'italic',
        fontFamily: 'Georgia, serif',
      }}>
        No workspace selected.
      </div>
    );
  }

  return (
    <Workstation
      userId={userId}
      supabase={supabase}
      userRecord={userRecord}
      workspaceSession={spaceSession}
      voiceConfig={voiceConfig}
      onExit={onLeave}
      onSidebarCollapse={onSidebarCollapse}
    />
  );
});

export default ChatWorkspaceFeature;
